import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { CertificateWidgetManagementDialog } from "./CertificateWidgetManagementDialog";

interface CertificateWidget {
  id: string;
  name: string;
  iframe_src: string;
  width: number;
  height: number;
  display_order: number;
  is_active: boolean;
}

export function CertificateWidgetManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState<CertificateWidget | null>(null);

  const { data: widgets, isLoading } = useQuery({
    queryKey: ["admin-certificate-widgets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("certificate_widgets")
        .select("*")
        .order("display_order", { ascending: true });
      
      if (error) throw error;
      return data as CertificateWidget[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("certificate_widgets")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-certificate-widgets"] });
      queryClient.invalidateQueries({ queryKey: ["certificate-widgets"] });
      toast({
        title: "Success",
        description: "Certificate widget deleted successfully",
      });
      setDeleteDialogOpen(false);
      setSelectedWidget(null);
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async ({ id, newOrder }: { id: string; newOrder: number }) => {
      const { error } = await supabase
        .from("certificate_widgets")
        .update({ display_order: newOrder })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-certificate-widgets"] });
      queryClient.invalidateQueries({ queryKey: ["certificate-widgets"] });
      toast({
        title: "Success",
        description: "Display order updated",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const handleReorder = (widget: CertificateWidget, direction: "up" | "down") => {
    if (!widgets) return;
    
    const currentIndex = widgets.findIndex((w) => w.id === widget.id);
    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= widgets.length) return;
    
    const targetWidget = widgets[targetIndex];
    
    reorderMutation.mutate({ id: widget.id, newOrder: targetWidget.display_order });
    reorderMutation.mutate({ id: targetWidget.id, newOrder: widget.display_order });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">Certificate Widgets</h2>
        <Button onClick={() => { setSelectedWidget(null); setDialogOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" />
          Add Widget
        </Button>
      </div>

      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Preview</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Dimensions</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {widgets?.map((widget, index) => (
              <TableRow key={widget.id}>
                <TableCell>
                  <div className="max-w-[150px] overflow-hidden">
                    <iframe
                      src={widget.iframe_src}
                      style={{
                        border: "none",
                        width: `${Math.min(widget.width, 150)}px`,
                        height: `${(widget.height * 150) / widget.width}px`,
                        transform: "scale(0.5)",
                        transformOrigin: "top left",
                      }}
                      title={widget.name}
                    />
                  </div>
                </TableCell>
                <TableCell className="font-medium">{widget.name}</TableCell>
                <TableCell>{widget.width} × {widget.height}</TableCell>
                <TableCell>{widget.display_order}</TableCell>
                <TableCell>
                  <Badge variant={widget.is_active ? "default" : "secondary"}>
                    {widget.is_active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleReorder(widget, "up")}
                      disabled={index === 0}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleReorder(widget, "down")}
                      disabled={index === widgets.length - 1}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSelectedWidget(widget); setDialogOpen(true); }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSelectedWidget(widget); setDeleteDialogOpen(true); }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <CertificateWidgetManagementDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        widget={selectedWidget}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Certificate Widget</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedWidget?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedWidget && deleteMutation.mutate(selectedWidget.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}